import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, RotateCcw, Maximize2 } from "lucide-react";
import rrwebPlayer from "rrweb-player";
import "rrweb-player/dist/style.css";

function ReplayPlayer({ replayData, onClose }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!replayData || !replayData.events || replayData.events.length === 0) {
      setIsLoading(false);
      return;
    }

    try {
      // Initialize the player
      const player = new rrwebPlayer({
        target: containerRef.current,
        props: {
          events: replayData.events,
          width: "100%",
          height: "100%",
          autoPlay: false,
          showController: true,
          skipInactive: true,
          speed: 1,
          mouseTail: {
            duration: 500,
            strokeStyle: "#06b6d4",
          },
        },
      });

      playerRef.current = player;

      // Listen to play/pause events
      player.addEventListener("play", () => setIsPlaying(true));
      player.addEventListener("pause", () => setIsPlaying(false));

      setIsLoading(false);
    } catch (error) {
      console.error("Failed to initialize replay player:", error);
      setIsLoading(false);
    }

    return () => {
      if (playerRef.current) {
        // Clean up player
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [replayData]);

  const togglePlay = () => {
    if (playerRef.current) {
      if (isPlaying) {
        playerRef.current.pause();
      } else {
        playerRef.current.play();
      }
    }
  };

  const reset = () => {
    if (playerRef.current) {
      playerRef.current.goto(0);
      playerRef.current.pause();
      setIsPlaying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-900 rounded-lg border border-gray-800">
        <div className="text-gray-400">Loading replay...</div>
      </div>
    );
  }

  if (!replayData || !replayData.events || replayData.events.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-900 rounded-lg border border-gray-800">
        <div className="text-gray-400">No replay data available</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-950 rounded-lg border border-gray-800 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-900/50">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium text-white">Session Replay</span>
          <span className="text-xs text-gray-500">
            {replayData.events.length} events
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={togglePlay}
            className="p-1.5 rounded hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
          >
            {isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={reset}
            className="p-1.5 rounded hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Player Container */}
      <div className="relative">
        <div
          ref={containerRef}
          className="w-full"
          style={{ height: "480px" }}
        />
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-800 bg-gray-900/50">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Recorded session</span>
          <span>Drag to scrub through timeline</span>
        </div>
      </div>
    </div>
  );
}

export default ReplayPlayer;
